import axios from "axios";
import authService from "./authService";
import { API_BASE_URL } from "../config/apiConfig";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// 요청 인터셉터: 모든 요청에 인증 토큰 추가
api.interceptors.request.use(
  (config) => {
    const authHeader = authService.getAuthHeader();
    if (authHeader.Authorization) {
      config.headers.Authorization = authHeader.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 시 로그아웃 처리
let isRedirecting = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;

      // 401: 인증 실패 (쿠키 없음/만료) → 세션 만료 처리
      if (status === 401) {
        const path = window.location.pathname;
        const isLoginPage = path === "/login" || path === "/adminlogin";
        const isAuthApi = error.config?.url?.includes('/auth/');

        // 로그인/인증 API 에러는 호출부에서 처리 (리다이렉트하지 않음)
        if (!isLoginPage && !isAuthApi && !isRedirecting) {
          isRedirecting = true;
          localStorage.removeItem('user');
          localStorage.removeItem('jwt_token');
          sessionStorage.removeItem('user');
          window.dispatchEvent(new CustomEvent('auth:session-expired', {
            detail: { message: '세션이 만료되었습니다. 다시 로그인해주세요.' }
          }));

          // 관리자/판매자 페이지는 관리자 로그인으로, 그 외는 일반 로그인으로
          if (path.startsWith("/admin") || path.startsWith("/seller")) {
            window.location.href = "/adminlogin";
          } else {
            window.location.href = "/login";
          }
        }
      }
      // 403: 권한 부족 → 로그아웃하지 않음 (호출부에서 처리)
    }
    return Promise.reject(error);
  }
);

// ========================================
// 공개 API (인증 불필요)
// ========================================

// Products - 공개
// Pagination params added: page (0-indexed), size
export const getProducts = (subdomain = null, page = 0, size = 30, sort = 'id,desc') => {
  const params = subdomain ? { subdomain, page, size, sort } : { page, size, sort };
  return api.get("/public/products", { params });
};
export const searchProducts = (keyword, subdomain = null, page = 0, size = 30, sort = 'id,desc') => {
  const params = subdomain ? { keyword, subdomain, page, size, sort } : { keyword, page, size, sort };
  return api.get("/public/products/search", { params });
};
export const getProductById = (id, config) => api.get(`/public/products/${id}`, config);
export const getProductByCode = (code) => api.get(`/public/products/code/${code}`);
export const getNextProductCode = (prefix) => api.get("/products/next-code", { params: { prefix } });
export const getProductsByCategory = (category, subdomain = null, page = 0, size = 30, sort = 'id,desc') => {
  const params = subdomain ? { subdomain, page, size, sort } : { page, size, sort };
  return api.get(`/public/products/category/${category}`, { params });
};
export const getProductsBySeller = (sellerId) =>
  api.get(`/public/products/seller/${sellerId}`);
export const getProductImages = (productId) => 
  api.get(`/public/products/${productId}/images`);

// Product Management (Generic / Seller)
export const createProductGeneric = (product) => api.post("/products", product);
export const updateProductGeneric = (id, product) => api.put(`/products/${id}`, product);
export const deleteProductGeneric = (id) => api.delete(`/products/${id}`);
export const toggleProductStatus = (id) => api.put(`/products/${id}/toggle`);

// Shop - 공개 (CommonPolicy BANK_INFO 사용)
export const getShopInfo = () => api.get("/public/common-policies/type/BANK_INFO");

// Orders - 공개 (비회원 주문 조회)
export const lookupGuestOrder = (orderNumber, phoneNumber) => 
  api.post("/public/orders/lookup", { orderNumber, phoneNumber });

export const findGuestOrderNumber = (data) => 
  api.post("/orders/find-number", data);


// Sellers - 공개
export const getSellerBySubdomain = async (subdomain) => {
  const response = await api.get(`/public/sellers/subdomain/${subdomain}`);
  return response.data;
};
export const checkSubdomainAvailability = async (subdomain) => {
  const response = await api.get(
    `/public/sellers/check-subdomain/${subdomain}`
  );
  return response.data;
};
export const checkSellerUsernameAvailability = async (username) => {
  const response = await api.get(`/public/sellers/check-username/${username}`);
  return response.data;
};

// Banners - 공개
export const getPublicBanners = (position) => api.get(`/public/banners/position/${position}`);
export const getSellerBanners = (subdomain) => api.get(`/public/banners/seller/${subdomain}`);

// ========================================
// 사용자 API (인증 필요)
// ========================================

// Orders - 사용자
export const createOrder = (order) => api.post("/orders", order);
export const getUserOrders = () => api.get("/orders/my"); // Updated to use OrderController
export const getUserOrderById = (id) => api.get(`/user/orders/${id}`); // This might need update if OrderController doesn't have /user/orders/id mapped
// Note: Backend OrderController has /{id}, so likely /api/orders/{id}. 
// But /user/orders prefix suggests another controller or gateway. 
// Standardizing on /api/orders for now based on OrderController @RequestMapping.
export const getOrder = (id) => api.get(`/orders/${id}`);
export const confirmOrder = (id) => api.post(`/orders/${id}/confirm`);


// Inquiries - 사용자
export const getMyInquiries = () => api.get("/inquiries/my");
export const createInquiry = (inquiry) => api.post("/inquiries", inquiry);
export const getGuestInquiries = (email, password) => api.post("/inquiries/guest", { email, password });

// Coupons - 사용자
export const getMyCoupons = () => api.get("/coupons/my");
export const getMyAvailableCoupons = () => api.get("/coupons/my/available");

// Customers - 사용자
export const createCustomer = (customer) => api.post("/customers", customer);
export const updateCustomer = (id, customer) =>
  api.put(`/customers/${id}`, customer);

// ========================================
// 관리자 API (ADMIN 권한 필요)
// ========================================

// Products - 관리자
export const createProduct = (product) => api.post("/admin/products", product);
export const updateProduct = (id, product) =>
  api.put(`/admin/products/${id}`, product);
export const deleteProduct = (id) => api.delete(`/admin/products/${id}`);
export const toggleProductSuspension = (id) => api.put(`/admin/products/${id}/suspend`);
export const getAllProducts = (source) => api.get("/admin/products", { params: { source } });
export const getAdminProductById = (id) => api.get(`/admin/products/${id}`);
export const addProductImage = (productId, imageData) => 
  api.post(`/admin/products/${productId}/images`, imageData);
export const deleteProductImage = (imageId) => 
  api.delete(`/admin/products/images/${imageId}`);

export const getAdminProductStats = () => api.get("/admin/products/stats");

// Inquiries - 관리자
export const getAdminInquiries = () => api.get("/inquiries/admin");
export const answerInquiry = (id, answer) => api.put(`/inquiries/admin/${id}/answer`, { answer });

// Sellers - 관리자
export const getSellers = () => api.get("/admin/sellers");
export const getSellerById = (id) => api.get(`/admin/sellers/${id}`);
export const impersonateSeller = (id) => api.post(`/admin/sellers/${id}/impersonate`);
export const createSeller = (seller) => api.post("/admin/sellers", seller);
export const updateSeller = (id, seller) =>
  api.put(`/admin/sellers/${id}`, seller);
export const deleteSeller = (id) => api.delete(`/admin/sellers/${id}`);

// Customers - 관리자
export const getCustomers = () => api.get("/admin/customers");
export const getCustomerById = (id) => api.get(`/admin/customers/${id}`);
export const getCustomerByEmail = (email) =>
  api.get(`/admin/customers/email/${email}`);

// Orders - 관리자
export const getOrders = (params) => api.get("/admin/orders", { params });
export const getOrderById = (id) => api.get(`/admin/orders/${id}`);
export const getOrdersByCustomerId = (customerId) =>
  api.get(`/admin/orders/customer/${customerId}`);
export const updateOrderStatus = (id, status) =>
  api.put(`/admin/orders/${id}/status`, null, { params: { status } });

// ========================================
// 판매자 상품 API (SELLER, ADMIN 권한 필요)
// ========================================

// 판매자의 모든 상품 조회
export const getSellerProducts = (sellerId) => 
  api.get(`/seller-products/seller/${sellerId}`);

// 판매자의 특정 상품 조회
export const getSellerProduct = (sellerId, productId) => 
  api.get(`/seller-products/seller/${sellerId}/product/${productId}`);

// 판매자 상품 등록/수정 (가격 설정)
export const createOrUpdateSellerProduct = (data) => 
  api.post("/seller-products", data);

// 판매자 상품 가격 변경
export const updateSellerProductPrice = (sellerProductId, price) => 
  api.put(`/seller-products/${sellerProductId}/price`, { price });

// 판매자 상품 재고 업데이트
export const updateSellerProductStock = (sellerProductId, stockQuantity) => 
  api.put(`/seller-products/${sellerProductId}/stock`, { stockQuantity });

// 판매자 상품 활성화/비활성화
export const toggleSellerProductStatus = (sellerProductId) => 
  api.put(`/seller-products/${sellerProductId}/toggle`);

// 판매자 상품 삭제
export const deleteSellerProduct = (sellerProductId) => 
  api.delete(`/seller-products/${sellerProductId}`);

// 특정 상품을 판매하는 모든 판매자 조회
export const getSellersForProduct = (productId) => 
  api.get(`/seller-products/product/${productId}/sellers`);

// 판매자 상품의 정산 금액 조회
export const getSellerProductCommission = (sellerProductId) => 
  api.get(`/seller-products/${sellerProductId}/commission`);

// 판매자 상품 통계 조회
export const getSellerProductStats = (sellerId) => 
  api.get(`/seller-products/seller/${sellerId}/stats`);

// 내 정보 조회 (판매자)
export const getMySellerInfo = () => api.get("/sellers/me");

// 내 정보 조회 (일반 유저 - 최신 정보)
export const getMyProfile = () => api.get("/auth/me");

// 내 정보 수정 (일반 유저)
export const updateUserProfile = (data) => api.put("/users/me", data);

// 비밀번호 변경
export const changeUserPassword = (data) => api.put("/users/password", data);

// ========================================
export const getAllUsers = () => api.get("/admin/users");

// 섹션 관련 API (ADMIN 권한 필요)
// ========================================

// 모든 공통 정책 조회
export const getAllCommonPolicies = () => 
  api.get("/admin/common-policies");

// 특정 유형의 정책 조회
// 특정 유형의 정책 조회
export const getCommonPolicyByType = (type) => 
  api.get(`/public/common-policies/type/${type}`);

export const getActiveSections = () => api.get("/sections/active");

// 공통 정책 생성 또는 업데이트
export const createOrUpdateCommonPolicy = (data) =>
  api.post("/admin/common-policies", data);

// 공통 정책 업데이트
export const updateCommonPolicy = (id, content) => 
  api.put(`/admin/common-policies/${id}`, { content });

// 공통 정책 활성화/비활성화
export const toggleCommonPolicyStatus = (id) => 
  api.put(`/admin/common-policies/${id}/toggle`);

// 공통 정책 삭제
export const deleteCommonPolicy = (id) => 
  api.delete(`/admin/common-policies/${id}`);

// ========================================
// 배송지 관리 API
// ========================================

// 내 배송지 목록 조회
export const getMyDeliveryAddresses = () => api.get("/delivery-addresses");

// 기본 배송지 조회
export const getDefaultDeliveryAddress = () => api.get("/delivery-addresses/default");

// 배송지 상세 조회
export const getDeliveryAddress = (id) => api.get(`/delivery-addresses/${id}`);

// 배송지 생성
export const createDeliveryAddress = (address) => api.post("/delivery-addresses", address);

// 배송지 수정
export const updateDeliveryAddress = (id, address) => api.put(`/delivery-addresses/${id}`, address);

// 배송지 삭제
export const deleteDeliveryAddress = (id) => api.delete(`/delivery-addresses/${id}`);

// 기본 배송지로 설정
export const setDefaultDeliveryAddress = (id) => api.patch(`/delivery-addresses/${id}/set-default`);

// 카테고리 설정 조회 (공개)
export const getCategorySettings = () => api.get("/public/category-settings");

// ========================================
// 적립금 포인트 API
// ========================================

// 내 적립금 내역 조회
export const getMyPointHistory = (page = 0, size = 10) => 
  api.get("/points/history", { params: { page, size } });

// ========================================
// 리뷰 API
// ========================================

// 리뷰 작성
export const createReview = (reviewData) => api.post("/reviews", reviewData);

// 내 리뷰 조회
export const getMyReviews = (page = 0, size = 10) => 
  api.get("/reviews/my-reviews", { params: { page, size } });

// 상품별 리뷰 조회
export const getProductReviews = (productId, page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') => 
  api.get(`/reviews/product/${productId}`, { params: { page, size, sortBy, sortDir } });

// 주문별 리뷰 조회
export const getReviewByOrder = (orderId) => api.get(`/reviews/order/${orderId}`);

// 리뷰 수정
export const updateReview = (reviewId, reviewData) => api.put(`/reviews/${reviewId}`, reviewData);

// 리뷰 삭제
export const deleteReview = (reviewId) => api.delete(`/reviews/${reviewId}`);

// 리뷰 작성 여부 확인
export const checkReviewed = (productId) => api.get(`/reviews/check/${productId}`);

// 파일 업로드는 fileService.js 사용

export default api;
